/**
 * SPA Application Entry Point
 *
 * Pure Vue 3 SPA with Vue Router, Pinia, and TanStack Query.
 * All routing is handled client-side with API calls to Laravel backend.
 */

import '../css/app.css';

import { VueQueryPlugin } from '@tanstack/vue-query';
import { createApp } from 'vue';
import App from './App.vue';
import { router } from './router';
import { createPiniaStore, useAuthStore } from './stores';
import { getThemeManager } from './lib/theme/ThemeManager';
import { api } from './lib/api/client';

// Create Vue app
const app = createApp(App);

// Create and use Pinia store
const pinia = createPiniaStore();
app.use(pinia);

// Use Vue Router
app.use(router);

// Use TanStack Query for server state
app.use(VueQueryPlugin, {
    queryClientConfig: {
        defaultOptions: {
            queries: {
                staleTime: 1000 * 60, // 1 minute
                gcTime: 1000 * 60 * 5, // 5 minutes
                retry: 1,
                refetchOnWindowFocus: true,
            },
        },
    },
});

// Initialize auth state from persisted storage or fetch from API
const authStore = useAuthStore(pinia);

// If we have persisted auth state, the 401 interceptor will handle
// session expiration automatically on the first API call.
// No need to verify on every page load - this was causing unnecessary
// logouts on transient errors during Vite HMR/reloads.

// Initialize theme manager (FOIT script already set attributes, this ensures JS state is in sync)
const themeManager = getThemeManager();
themeManager.cleanupLegacyStorage();

// Mount app
app.mount('#app');
